import { HttpError } from '@pickleballinc/lib/http-error';
import { useMutation } from '@tanstack/react-query';

import { NotificationsState } from '@/modules/players/v2/tabs/edit-profile/accounts-i-manage/types';
import { IchangePassword } from '@/modules/players/v2/tabs/edit-profile/update-password/UpdatePassword';
import { UserBioCreation, UserImageCreation, UserV2 } from '@/types/players.types';

const ENDPOINT = `/modules/players/v2/tabs/edit-profile/hooks`;

async function updateUser({ uuid, data }: { uuid: string; data: Partial<UserV2> }) {
  const fetchURL = `/api/v2/players/updateUser?uuid=${uuid}`;
  const response = await fetch(fetchURL, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.message;
}

async function updatePassword({
  uuid,
  userAgent,
  data,
}: {
  uuid: string | undefined;
  userAgent: string;
  data: IchangePassword;
}) {
  const bodyMessage = JSON.stringify({
    email: data.email,
    password: data.password,
    oldPassword: data.oldPassword,
  });
  const fetchURL = `/api/v2/players/updatePassword?uuid=${uuid}&userAgent=${userAgent}`;
  const response = await fetch(fetchURL, {
    method: 'POST',
    body: bodyMessage,
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.message;
}

async function createBio({ uuid, data }: { uuid: string | undefined; data: UserBioCreation }) {
  const bodyMessage = JSON.stringify({
    userUuid: uuid,
    bioText: data.bioText,
    firstName: data.firstName,
    lastName: data.lastName,
  });

  const fetchURL = `/api/v2/players/createBio?uuid=${uuid}`;

  const response = await fetch(fetchURL, {
    method: 'POST',
    body: bodyMessage,
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.result;
}

async function createUserImage({
  uuid,
  data,
}: {
  uuid: string | undefined;
  data: UserImageCreation;
}) {
  const bodyMessage = JSON.stringify({
    userUuid: uuid,
    type: data.type,
    url: data.url,
    firstName: data.firstName,
    lastName: data.lastName,
  });

  const fetchURL = `/api/v2/players/createUserImage?uuid=${uuid}`;

  const response = await fetch(fetchURL, {
    method: 'POST',
    body: bodyMessage,
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.result;
}

async function deleteUserImage({
  uuid,
  imageUuid,
}: {
  uuid: string | undefined;
  imageUuid: string | undefined;
}) {
  const fetchURL = `/api/v2/players/deleteUserImage?uuid=${uuid}&imageUuid=${imageUuid}`;

  const response = await fetch(fetchURL, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.message;
}

async function removeAccountFromManagingMe({
  managerUserUuid,
  managedUserUuid,
  isPrimaryParentImpersonator,
}: {
  managerUserUuid: string;
  managedUserUuid: string;
  isPrimaryParentImpersonator: boolean;
}) {
  const response = await fetch(
    `/api/v2/players/removeAccountFromManagingMe?managerUserUuid=${managerUserUuid}&managedUserUuid=${managedUserUuid}&isPrimaryParentImpersonator=${isPrimaryParentImpersonator}`,
    {
      method: 'DELETE',
    }
  );
  const responseData = await response.json();
  return responseData.data.message;
}

async function revokeManagingRequest({
  requestUuid,
  managedUserUuid,
}: {
  requestUuid: string;
  managedUserUuid: string;
}) {
  const response = await fetch(
    `/api/v2/players/revokeManagingRequest?requestUuid=${requestUuid}&managedUserUuid=${managedUserUuid}`,
    {
      method: 'DELETE',
    }
  );
  const responseData = await response.json();
  return responseData.data.message;
}

async function removeAccountIManage({
  managedUserUuid,
  managerUserUuid,
}: {
  managedUserUuid: string;
  managerUserUuid: string;
}) {
  const response = await fetch(
    `/api/v2/players/removeAccountIManage?managedUserUuid=${managedUserUuid}&managerUserUuid=${managerUserUuid}`,
    {
      method: 'DELETE',
    }
  );
  const responseData = await response.json();
  return responseData.data.message;
}

async function sendUserManageAccountInvite({
  managedUserUuid,
  newManagerUserUuid,
  isPrimaryParentImpersonator,
}: {
  managedUserUuid: string;
  newManagerUserUuid: string;
  isPrimaryParentImpersonator: boolean;
}) {
  const response = await fetch(
    `/api/v2/players/sendUserManageAccountInvite?managedUserUuid=${managedUserUuid}&newManagerUserUuid=${newManagerUserUuid}&isPrimaryParentImpersonator=${isPrimaryParentImpersonator}`,
    {
      method: 'POST',
    }
  );
  const responseData = await response.json();
  return responseData.data.message;
}

async function receiveNotificationsForAccountIManage({
  managedUserUuid,
  managerUserUuid,
  status,
}: {
  managedUserUuid: string;
  managerUserUuid: string;
  status: NotificationsState;
}) {
  const response = await fetch(
    `/api/v2/players/receiveNotificationsForAccountIManage?managerUserUuid=${managerUserUuid}&managedUserUuid=${managedUserUuid}&status=${status}`,
    {
      method: 'PATCH',
    }
  );
  const responseData = await response.json();
  return responseData.data.message;
}

async function updateUserAsManagerOrParent({
  uuid,
  data,
}: {
  uuid: string;
  data: Partial<UserV2>;
}) {
  const fetchURL = `/api/v2/players/updateUserAsManagerOrParent?uuid=${uuid}`;
  const response = await fetch(fetchURL, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    const loggerMessage = `Status ${response.status} when calling ${ENDPOINT}. Fetch URL ${fetchURL}. Error message: ${errorMessage}`;
    throw new HttpError({
      message: loggerMessage,
      statusCode: response.status,
    });
  }

  const responseData = await response.json();
  return responseData.data.message;
}

export const useUpdateUser = () => useMutation(updateUser);
export const useUpdateUserAsManagerOrParent = () => useMutation(updateUserAsManagerOrParent);
export const useUpdatePassword = () => useMutation(updatePassword);
export const useCreateBio = () => useMutation(createBio);
export const useCreateUserImage = () => useMutation(createUserImage);
export const useDeleteUserImage = () => useMutation(deleteUserImage);
export const useRemoveAccountFromManagingMe = () => useMutation(removeAccountFromManagingMe);
export const useRevokeManagingAccount = () => useMutation(revokeManagingRequest);
export const useRemoveAccountIManage = () => useMutation(removeAccountIManage);
export const useSendUserManageAccountInvite = () => useMutation(sendUserManageAccountInvite);
export const useReceiveNotificationsForAccountIManage = () =>
  useMutation(receiveNotificationsForAccountIManage);
